import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PersonalizedPromptDescriptor } from '@bower/shared';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';
import {
  personalizedPromptAnalyticsProperties,
  type PersonalizedPromptAnalyticsSource,
} from '../../utils/personalizedPromptSelection';

/**
 * Tracks prompt suggestions through one privacy-safe event contract. An
 * unchanged ordered set emits once per mount; a genuinely different visible
 * set emits a fresh set of impressions.
 */
export function useSuggestedPromptAnalytics(
  prompts: readonly PersonalizedPromptDescriptor[],
  source: PersonalizedPromptAnalyticsSource,
) {
  const visibleSetSignature = useMemo(
    () =>
      [
        source,
        ...prompts.map(
          (prompt, position) => `${position}:${prompt.analyticsTemplateId}:${prompt.key}`,
        ),
      ].join('|'),
    [prompts, source],
  );
  const lastTrackedSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (prompts.length === 0 || visibleSetSignature === lastTrackedSignatureRef.current) return;
    lastTrackedSignatureRef.current = visibleSetSignature;
    prompts.forEach((prompt, position) => {
      safeAnalytics.track(ANALYTICS_EVENTS.SUGGESTED_PROMPT_SHOWN, {
        ...personalizedPromptAnalyticsProperties(prompt, source, position),
      });
    });
  }, [prompts, source, visibleSetSignature]);

  return useCallback(
    (prompt: PersonalizedPromptDescriptor, position: number) => {
      safeAnalytics.track(ANALYTICS_EVENTS.SUGGESTED_PROMPT_CLICKED, {
        ...personalizedPromptAnalyticsProperties(prompt, source, position),
      });
    },
    [source],
  );
}
